import React, { useEffect, useRef, useState } from 'react';
import { resumeService } from '../services/api';
import type { Resume } from '../types';
import { FileText, Loader2 } from 'lucide-react';
import { augmentPreviewHtml } from '../utils/previewHtml';

const DESIGN_WIDTH = 1000;

interface Props {
  resume: Resume;
}

interface Box {
  w: number;
  h: number;
  scale: number;
}

const ResumeThumb: React.FC<Props> = ({ resume }) => {
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [box, setBox] = useState<Box | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let cancelled = false;
    resumeService
      .previewSavedResume(resume as unknown as Resume)
      .then((response) => {
        if (!cancelled) setHtml(augmentPreviewHtml(response.data));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [resume]);

  const measure = () => {
    const frame = iframeRef.current;
    const doc = frame?.contentDocument;
    if (!frame || !doc || !doc.body) return;
    const w = Math.max(doc.body.scrollWidth, doc.documentElement?.scrollWidth ?? 0);
    const h = Math.max(doc.body.scrollHeight, doc.documentElement?.scrollHeight ?? 0);
    const cw = containerRef.current?.clientWidth ?? 0;
    if (w > 0 && h > 0 && cw > 0) {
      setBox({ w, h, scale: cw / w });
    }
  };

  if (failed) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-zinc-300">
        <FileText className="w-5 h-5" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden">
      {html ? (
        <iframe
          ref={iframeRef}
          title={`${resume.personalInfo.fullName} preview`}
          srcDoc={html}
          sandbox="allow-same-origin"
          onLoad={measure}
          tabIndex={-1}
          aria-hidden
          scrolling="no"
          className="absolute top-0 left-0 border-none pointer-events-none"
          style={
            box
              ? {
                  width: box.w,
                  height: box.h,
                  transform: `scale(${box.scale})`,
                  transformOrigin: 'top left',
                }
              : { width: DESIGN_WIDTH }
          }
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-zinc-300">
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      )}
    </div>
  );
};

export default ResumeThumb;